import _extends from "@babel/runtime/helpers/esm/extends";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["block", "disabledDays", "downcaseMonth", "locale", "month", "monthSelectRef", "multiple", "navbarType", "onChange", "onChangeMonth", "onClose", "readOnly", "selectable", "theme", "value", "withRange", "hideDisabledYears", "yearSelectRef", "isResettableRange", "classes"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import classNames from 'classnames/bind';
import { defaultPropObject } from "../constants";
import { checkIsDisabledRange, formatDate, getDisabledTimestamps, transformDisabledDays } from "../utils/date";
import { getDayAtRange, getIsSelectingFirstDay } from "../utils/date/getters";
import { getLocale, localeUtils } from "../utils/date/locale";
import noop from "../utils/noop";
import { isArray } from "../utils/type";
import { Navbar, Weekday } from './components';
import { locales, navbarTypes, themes } from './constants';
import { isMinDateGreatThanMaxOne, parseRange, parseValue } from './helpers';
import styles from './Datepicker.css';
var cx = classNames.bind(styles);
var dayCls = {
  disabled: cx('Day_disabled'),
  outside: cx('Day_outside'),
  selected: cx('Day_selected'),
  today: cx('Day_today'),
  start: cx('Day_start'),
  end: cx('Day_end'),
  inRange: cx('Day_inRange'),
  firstDayInLayout: cx('Day_firstDayInLayout'),
  lastDayInLayout: cx('Day_lastDayInLayout')
};
var CLASSNAMES = {
  weekdaysRow: cx('Weekday__row'),
  body: cx('Body'),
  day: cx('Day'),
  month: cx('Month'),
  today: cx('Today'),
  week: cx('Week'),
  weekday: cx('Weekday'),
  weekdays: cx('Weekdays'),
  weekNumber: cx('WeekNumber'),
  selected: dayCls.selected,
  disabled: dayCls.disabled,
  outside: dayCls.outside
};
var disabledDaysWhenMinGreatThanMax = [new Date(), {
  before: new Date(),
  after: new Date()
}];
var Datepicker = /*#__PURE__*/memo(function (_ref) {
  var _cx;

  var block = _ref.block,
      disabledDays = _ref.disabledDays,
      _ref$downcaseMonth = _ref.downcaseMonth,
      downcaseMonth = _ref$downcaseMonth === void 0 ? true : _ref$downcaseMonth,
      _ref$locale = _ref.locale,
      locale = _ref$locale === void 0 ? locales[1] : _ref$locale,
      monthProp = _ref.month,
      monthSelectRef = _ref.monthSelectRef,
      _ref$multiple = _ref.multiple,
      multiple = _ref$multiple === void 0 ? false : _ref$multiple,
      _ref$navbarType = _ref.navbarType,
      navbarType = _ref$navbarType === void 0 ? navbarTypes[0] : _ref$navbarType,
      onChange = _ref.onChange,
      onChangeMonth = _ref.onChangeMonth,
      onClose = _ref.onClose,
      readOnly = _ref.readOnly,
      selectable = _ref.selectable,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? themes[1] : _ref$theme,
      value = _ref.value,
      withRange = _ref.withRange,
      hideDisabledYears = _ref.hideDisabledYears,
      yearSelectRef = _ref.yearSelectRef,
      isResettableRange = _ref.isResettableRange,
      _ref$classes = _ref.classes,
      classes = _ref$classes === void 0 ? defaultPropObject : _ref$classes,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(parseRange(value)),
      _useState2 = _slicedToArray(_useState, 2),
      rangeData = _useState2[0],
      setRangeData = _useState2[1];

  var localeData = getLocale(locale);
  var selectedDays = withRange ? [rangeData.from, {
    from: rangeData.from,
    to: rangeData.enteredTo
  }] : parseValue(value, multiple);
  var transformedDisabledDays = transformDisabledDays(disabledDays);
  var disabledDaysTimestampMap = useMemo(function () {
    return getDisabledTimestamps(disabledDays);
  }, [disabledDays]);

  var _useState3 = useState(monthProp || isArray(selectedDays) ? selectedDays[0] : selectedDays),
      _useState4 = _slicedToArray(_useState3, 2),
      month = _useState4[0],
      setMonth = _useState4[1];

  var containerRef = useRef(null);
  useEffect(function () {
    var container = containerRef.current;

    if (container) {
      var handleKeyDown = function handleKeyDown(e) {
        e.stopPropagation();
      };

      container.addEventListener('keydown', handleKeyDown);
      return function () {
        if (container) {
          container.removeEventListener('keydown', handleKeyDown);
        }
      };
    }

    return noop;
  }, []);
  useEffect(function () {
    setRangeData(parseRange(value));
  }, [value]);
  useEffect(function () {
    setMonth(monthProp || (isArray(selectedDays) ? selectedDays[0] : selectedDays)); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthProp]);
  var handleChangeMonth = useCallback(function (newMonth) {
    setMonth(newMonth);

    if (onChangeMonth) {
      onChangeMonth(newMonth);
    }
  }, [onChangeMonth]);
  var firstDayOfWeek = localeData.firstDayOfWeek;
  var modifiers = useMemo(function () {
    var _ref2;

    var defineFirstDayInLayoutForRange = function defineFirstDayInLayoutForRange(day) {
      return !!(getDayAtRange(day, rangeData) && day.getDay() === firstDayOfWeek);
    };

    var defineLastDayInLayoutForRange = function defineLastDayInLayoutForRange(day) {
      var lastDay = firstDayOfWeek === 0 ? 6 : 0;
      return !!(getDayAtRange(day, rangeData) && day.getDay() === lastDay);
    };

    return _objectSpread({}, withRange ? (_ref2 = {}, _defineProperty(_ref2, dayCls.inRange, {
      from: rangeData.from && new Date(rangeData.from.getTime()),
      to: rangeData.enteredTo && new Date(rangeData.enteredTo.getTime())
    }), _defineProperty(_ref2, dayCls.start, rangeData.from), _defineProperty(_ref2, dayCls.end, rangeData.enteredTo), _defineProperty(_ref2, dayCls.firstDayInLayout, defineFirstDayInLayoutForRange), _defineProperty(_ref2, dayCls.lastDayInLayout, defineLastDayInLayoutForRange), _ref2) : {});
  }, [rangeData, withRange, firstDayOfWeek]);

  var handleDayClick = function handleDayClick(day, _ref3) {
    var selected = _ref3[dayCls.selected],
        disabled = _ref3[dayCls.disabled];

    if (disabled || readOnly) {
      return;
    }

    var target;
    var isValid = false;
    var formattedDay = formatDate(day);

    if (withRange) {
      var _DateUtils$addDayToRa = DateUtils.addDayToRange(day, rangeData),
          from = _DateUtils$addDayToRa.from,
          to = _DateUtils$addDayToRa.to;

      if (checkIsDisabledRange(disabledDaysTimestampMap, {
        from: from,
        to: to
      })) {
        setRangeData({
          from: day,
          to: undefined,
          enteredTo: undefined
        });
        target = [formatDate(day), ''];
      } else if (isResettableRange && rangeData.from && rangeData.to) {
        target = [formatDate(day), ''];
      } else {
        target = [from ? formatDate(from) : formatDate(day), to ? formatDate(to) : ''];
      }

      if (target[1]) {
        isValid = true;
      }
    } else if (selected) {
      if (multiple) {
        var selectedIndex = value.findIndex(function (selectedDay) {
          return selectedDay === formattedDay;
        });
        target = value.filter(function (_, index) {
          return index !== selectedIndex;
        });
      } else {
        target = '';
      }
    } else if (multiple) {
      target = [].concat(_toConsumableArray(value), [formattedDay]);
    } else {
      target = formattedDay;
      isValid = true;
    }

    onChange(target, isValid);
    if (!multiple && isValid && onClose) onClose();
  };

  var handleDayMouseEnter = useMemo(function () {
    return withRange ? function (day) {
      var from = rangeData.from,
          to = rangeData.to;

      if (!getIsSelectingFirstDay(from, to, day) && !checkIsDisabledRange(disabledDaysTimestampMap, {
        from: from,
        to: day
      })) {
        setRangeData(_objectSpread(_objectSpread({}, rangeData), {}, {
          enteredTo: day
        }));
      }
    } : undefined;
  }, [disabledDaysTimestampMap, rangeData, withRange]);
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef
  }, /*#__PURE__*/React.createElement(DayPicker, _extends({
    showOutsideDays: true,
    fixedWeeks: true,
    weekdayElement: /*#__PURE__*/React.createElement(Weekday, null),
    navbarElement: /*#__PURE__*/React.createElement(Navbar, {
      disabledDaysTimestampMap: disabledDaysTimestampMap,
      downcaseMonth: downcaseMonth,
      month: month,
      monthSelectRef: monthSelectRef,
      onChangeMonth: handleChangeMonth,
      selectable: selectable,
      theme: theme,
      localeUtils: localeUtils,
      type: navbarType,
      hideDisabledYears: hideDisabledYears,
      yearSelectRef: yearSelectRef
    }),
    captionElement: function captionElement() {
      return null;
    },
    className: cx('Datepicker', (_cx = {}, _defineProperty(_cx, "Datepicker_theme_".concat(theme), theme), _defineProperty(_cx, "Datepicker_block", block), _defineProperty(_cx, "Datepicker_readonly", readOnly), _defineProperty(_cx, "Datepicker_dateRangePicker", withRange), _cx)),
    month: month,
    locale: locale,
    modifiers: modifiers,
    selectedDays: selectedDays,
    disabledDays: isMinDateGreatThanMaxOne(transformedDisabledDays) ? disabledDaysWhenMinGreatThanMax : transformedDisabledDays,
    onDayClick: handleDayClick // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    localeUtils: localeUtils // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    classNames: _objectSpread(_objectSpread({}, CLASSNAMES), {}, {
      wrapper: cx(classes.root)
    }),
    onDayMouseEnter: handleDayMouseEnter
  }, localeData, restProps)));
});
export default Datepicker;